import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import { store } from 'redux-rest-reducer';

import { USER_CONFIG, BATCHES_CONFIG } from 'constants/api';
import { Header, Message } from 'semantic-ui-react';
import Pagination from 'modules/list/pagination';

import styled from 'styled-components';
import { Separator, FlexView } from '@cuemath/comps';
import { get as getConfig } from 'actions/configs';
import { getAll as getPrograms } from 'actions/programs';
import TeacherBatch from 'modules/teacher-batch-view';
import { get as getTeacherSlot } from 'actions/teacher-slots';
import { get as getListCount } from 'actions/list-count';
import { openModal } from 'actions/modal';

import { get as getProfile } from 'actions/teacher-profile';
import { getAll as getCreds } from 'actions/creds';

import { get as getTeacher } from 'actions/teachers';
import { getAll as getTeacherClassrooms } from 'actions/teacher-classrooms';

import { HeaderHead } from 'modules/form';

import TeacherProfileForm from './profileForm';
import TeacherBasicForm from './basicForm';
import TeacherCredsForm from './credsForm';
import TeacherClassroom from './classrooms-list';
import { RESULTS_PER_PAGE } from 'constants/list';

const FullFlexView = styled(FlexView)`
  width: 100%;
`;

const HeaderHeadWrapper = styled(HeaderHead)`
  padding: 10px 10px 30px 10px;
`;

const TAB_TYPES = {
  BASIC_DETAILS_TAB: 'BASIC_DETAILS_TAB',
  CREDS_TAB: 'CREDS_TAB',
  CALENDAR_TAB: 'CALENDAR_TAB',
  CLASSROOM_TAB: 'CLASSROOM_TAB',
  TRIAL_PROFILE: 'TRIAL_PROFILE',
};

export const TabConfig = {
  'basic-details': TAB_TYPES.BASIC_DETAILS_TAB,
  'creds': TAB_TYPES.CREDS_TAB,
  'calendar': TAB_TYPES.CALENDAR_TAB,
  'classrooms': TAB_TYPES.CLASSROOM_TAB,
  'trial-profile': TAB_TYPES.TRIAL_PROFILE,
};

class TeacherForm extends PureComponent {
  static defaultProps = {
    userConfig: {},
    batchConfig: {},
    programs: [],
  };

  constructor(props) {
    super(props);

    this.state = {
      teacher: null,
      profileData: null,
      message: null,
      errorText: null,
      classroomsData: null,
      onDataFilter: null,
      page: 1,
      filterParam: [],
    };
  }

  static getDerivedStateFromProps(props, state) {
    const newState = {};
    const {
      teachers,
      teacherSlots,
      teacherProfile,
      actions,
      teacherClassrooms,
      listCount,
    } = props;
    const { teacherId } = props;
    const { profileData, page } = state;

    const listParams = { page, per_page: RESULTS_PER_PAGE };
    const countParams = { count: true };
    const profileGet = store.get(teacherProfile, teacherId);
    const teacherGet = store.get(teachers, teacherId, { embed: ['creds', 'psm_info'] });
    const slotGet = store.get(teacherSlots, teacherId);
    const classroomsGet = store.getAll(teacherClassrooms, teacherId, { listParams });

    const countUrl = `teachers/${teacherId}/teacher-classrooms`;
    const reviewCount = store.get(listCount, countUrl, countParams);

    if (reviewCount && reviewCount.isLoaded) {
      newState.countData = reviewCount;
    }

    if (slotGet && slotGet.isLoaded && !slotGet.isStale && slotGet.data)
      newState.teacherSlots = slotGet.data;
    else if (slotGet && slotGet.isStale) {
      actions.getTeacherSlot(teacherId, null, { teacher_id: teacherId });
    }

    if (teacherGet && teacherGet.isLoaded && !teacherGet.isStale) {
      newState.teacher = teacherGet.data;
    } else if (teacherGet && teacherGet.isLoadingFailed) {
      newState.errorText = store.getError(teacherGet)
        ? store.getError(teacherGet).message
        : 'Loading failed.';
    } else if (teacherGet && teacherGet.isStale) {
      actions.getTeacher(teacherId, { embed: ['creds', 'psm_info'] });
    }

    if (profileGet && profileGet.isLoaded && !profileData && !profileGet.isStale) {
      if (profileGet.data && Object.keys(profileGet.data).length) {
        newState.profileData = profileGet.data;
      } else {
        newState.errorText = 'Profile not found!';
      }
    }

    if (
      classroomsGet &&
      classroomsGet.isLoaded &&
      !classroomsGet.isStale &&
      classroomsGet.data
    ) {
      newState.classroomsData = classroomsGet.data;
    }

    return newState;
  }

  componentDidMount(prevProps, prevState) {
    const { actions } = this.props;
    const { teacherId } = this.props;
    actions.getConfig(USER_CONFIG);
    actions.getConfig(BATCHES_CONFIG);
    actions.getPrograms();

    const { profileData, teacher, page, countData } = this.state;

    const listParams = { page, per_page: RESULTS_PER_PAGE };

    const countParams = { count: true };
    const countFilterParams = { count: true, page, per_page: RESULTS_PER_PAGE };

    if (!countData)
      actions.getListCount(
        `teachers/${teacherId}/teacher-classrooms`,
        countParams,
        countFilterParams,
      );

    if (!profileData) actions.getProfile(teacherId);
    if (!teacher) actions.getTeacher(teacherId, { embed: ['creds', 'psm_info'] });

    if (teacherId) actions.getTeacherSlot(teacherId, null, { teacher_id: teacherId });
    actions.getTeacherClassrooms(teacherId, listParams);
    actions.getCreds();
  }

  componentDidUpdate(prevProps, prevState) {
    const { actions } = this.props;
    const { teacherId } = this.props;
    const { page, listData, filterParam, onDataFilter } = this.state;

    const listParams = { page, per_page: RESULTS_PER_PAGE };
    const countParams = { count: true };
    const countFilterParams = { count: true, page, per_page: RESULTS_PER_PAGE };

    if (filterParam && filterParam.length && !onDataFilter) {
      filterParam.forEach(rec => {
        const key = rec.id;
        const value = rec.value;

        if (value) {
          const newValue = key === 'date' ? moment(value, 'YYYY-MM-DD').unix() : value;

          listParams[key] = newValue;
          countFilterParams[key] = newValue;
        }
      });
    }

    const isListStale = !!(listData && listData.isStale);

    if (prevState.page !== page || isListStale || filterParam !== prevState.filterParam) {
      actions.getTeacherClassrooms(teacherId, listParams);
      actions.getListCount(
        `teachers/${teacherId}/teacher-classrooms`,
        countParams,
        countFilterParams,
      );
    }
  }

  static propTypes = {
    profileData: PropTypes.object,
  };

  goToBack = () => {
    const { history } = this.props;
    history.push('/users');
  };

  renderbatchView = () => {
    const { teacherData, teacherSlots } = this.state;
    return (
      <TeacherBatch
        teacherBigForm={true}
        teacher={teacherData}
        uniqueSlots={teacherSlots}
      />
    );
  };

  onPageChange = newPage => {
    this.setState({ page: newPage });
  };

  onFilter = filters => {
    this.setState({ filterParam: filters });
  };

  render() {
    const { userConfig, batchConfig, programs, currentTab } = this.props;
    const {
      teacher,
      errorText,
      teacherSlots,
      page,
      classroomsData,
      countData,
    } = this.state;

    const isClassroom = !!classroomsData;

    let isReady = false;
    if (userConfig && batchConfig && programs && teacher) {
      if (
        Object.keys(userConfig).length &&
        Object.keys(batchConfig).length &&
        programs.length &&
        teacher
      ) {
        isReady = true;
      }
    }

    return (
      <div>
        {errorText && (
          <Message negative>
            <p>{errorText}</p>
          </Message>
        )}

        {isReady && TabConfig[currentTab] === TAB_TYPES.BASIC_DETAILS_TAB && (
          <TeacherBasicForm
            {...this.state}
            userConfig={userConfig}
            batchConfig={batchConfig}
            programs={programs}
          />
        )}
        {isReady && TabConfig[currentTab] === TAB_TYPES.BASIC_DETAILS_TAB && (
          <TeacherProfileForm
            {...this.state}
            userConfig={userConfig}
            batchConfig={batchConfig}
            programs={programs}
          />
        )}
        {isReady && TabConfig[currentTab] === TAB_TYPES.CREDS_TAB && (
          <TeacherCredsForm
            {...this.state}
            userConfig={userConfig}
            batchConfig={batchConfig}
            programs={programs}
          />
        )}
        {isReady && TabConfig[currentTab] === TAB_TYPES.CALENDAR_TAB && (
          <HeaderHeadWrapper>
            <Header as="h5" floated="right">
              <a
                href={`/teacher-students/${teacher.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                See Teacher's Students
              </a>
            </Header>
            <Header as="h3" floated="left">
              Teacher Calendar
            </Header>
          </HeaderHeadWrapper>
        )}
        <Separator transparent height="20" />
        {TabConfig[currentTab] === TAB_TYPES.CALENDAR_TAB && (
          <FullFlexView justifyContent="center" alignItems="center">
            {isReady && this.renderbatchView()}
          </FullFlexView>
        )}
        {isClassroom && TabConfig[currentTab] === TAB_TYPES.CLASSROOM_TAB && (
          <FullFlexView justifyContent="center" alignItems="center">
            <HeaderHeadWrapper>
              <Header as="h3" floated="left">
                Teacher Classrooms
              </Header>
            </HeaderHeadWrapper>
            <Separator transparent height="20" />
            <TeacherClassroom
              tableData={!!classroomsData ? classroomsData : []}
              countData={!!countData && !!countData.data ? countData.data : 0}
              style={{ height: 430, textAlign: 'center', width: '950px' }}
              onFilter={this.onFilter}
              onDateSearch={this.onDateSearch}
              page={page}
            />
            <Pagination
              page={page}
              perPage={RESULTS_PER_PAGE}
              onPageChange={this.onPageChange}
              count={!!countData && !!countData.data ? countData.data : 0}
            />
          </FullFlexView>
        )}
      </div>
    );
  }
}

export default connect(
  ({
    configs,
    programs,
    teacherProfile,
    teachers,
    teacherSlots,
    teacherClassrooms,
    listCount,
  }) => ({
    listCount,
    userConfig: store.get(configs, USER_CONFIG)
      ? store.get(configs, USER_CONFIG).data
      : null,
    batchConfig: store.get(configs, BATCHES_CONFIG)
      ? store.get(configs, BATCHES_CONFIG).data
      : null,
    programs: store.getAll(programs) ? store.getAll(programs).data : null,
    teacherProfile,
    teachers,
    teacherSlots,
    teacherClassrooms,
  }),
  dispatch => ({
    actions: bindActionCreators(
      {
        getConfig,
        getProfile,
        getTeacher,
        getTeacherSlot,
        openModal,
        getPrograms,
        getTeacherClassrooms,
        getListCount,
        getCreds,
      },
      dispatch,
    ),
  }),
)(TeacherForm);
